import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  GraduationCap, 
  Award, 
  BookOpen, 
  CheckCircle, 
  ArrowRight, 
  Mail, 
  Phone,
  MessageCircle,
  Star,
  FileText,
  Users,
  Target,
  TrendingUp,
  Clock,
  AlertCircle,
  Lightbulb,
  Calculator
} from "lucide-react";

export default function APIScoreGuide() {
  const whatsappLink = "https://wa.me/916284607500";

  return (
    <div data-testid="api-score-guide-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?crop=entropy&cs=srgb&fm=jpg&w=1920" 
            alt="Academic Career"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Star className="w-6 h-6 text-[#C5A059]" />
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Academic Career Growth</span>
              <Star className="w-6 h-6 text-[#C5A059]" />
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-medium text-white mb-6">
              API Score <span className="text-[#C5A059]">Guide</span>
            </h1>
            <p className="text-slate-300 text-lg mb-4 max-w-2xl mx-auto">
              Academic Performance Indicator - Your Path to Career Advancement
            </p>
            <p className="text-slate-400 max-w-3xl mx-auto">
              Comprehensive guide to understanding and maximizing your API score for faculty promotions under UGC CAS (Career Advancement Scheme) guidelines.
            </p>
          </div>
        </div>
      </section>

      {/* What is API Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Understanding API</span>
              <h2 className="font-serif text-4xl font-medium text-slate-900 mt-3 mb-6">
                What is Academic Performance Indicator?
              </h2>
              <div className="w-16 h-[3px] bg-[#C5A059] mb-8" />
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                The <strong>Academic Performance Indicator (API)</strong> is a scoring system introduced by the University Grants Commission (UGC) of India to evaluate the performance of faculty members in universities and colleges for career advancement and promotions.
              </p>
              <p className="text-slate-600 leading-relaxed mb-6">
                Under the <strong>Career Advancement Scheme (CAS)</strong>, faculty members are assessed based on their academic contributions across three major categories:
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700"><strong>Category I:</strong> Teaching, Learning and Evaluation</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700"><strong>Category II:</strong> Co-curricular, Extension & Professional Development</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#C5A059] mt-0.5" />
                  <span className="text-slate-700"><strong>Category III:</strong> Research and Academic Contributions</span>
                </li>
              </ul>
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                  <p className="text-amber-800 text-sm">
                    <strong>Important:</strong> API scores and requirements may vary based on UGC 2018/2025 regulations and individual university guidelines. Always verify with your institution's CAS ordinance.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 p-8">
              <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6">Minimum API Requirements</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 border-l-4 border-blue-500">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-900">Assistant Professor (Stage 1→2)</span>
                    <span className="bg-blue-500 text-white px-4 py-2 font-bold text-xl">75</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">4 Years Assessment Period</p>
                </div>
                <div className="bg-white p-4 border-l-4 border-purple-500">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-900">Assistant Professor (Stage 2→3)</span>
                    <span className="bg-purple-500 text-white px-4 py-2 font-bold text-xl">150</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">5 Years Assessment Period</p>
                </div>
                <div className="bg-white p-4 border-l-4 border-emerald-500">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-900">Associate Professor</span>
                    <span className="bg-emerald-500 text-white px-4 py-2 font-bold text-xl">300</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">3 Years Assessment Period</p>
                </div>
                <div className="bg-white p-4 border-l-4 border-[#C5A059]">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-slate-900">Professor</span>
                    <span className="bg-[#C5A059] text-slate-950 px-4 py-2 font-bold text-xl">500+</span>
                  </div>
                  <p className="text-slate-500 text-sm mt-1">3 Years Assessment Period</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Infographic 1 - Boost Your API Score */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-medium text-slate-900 mb-4">Boost Your API Score</h2>
            <p className="text-slate-600">Our services to maximize your API score and accelerate career growth</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/au95dl0f_WhatsApp%20Image%202026-06-10%20at%2010.45.58%20PM.jpeg" 
              alt="Boost Your API Score - SPA Publication Group Services"
              className="max-w-4xl w-full shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* API Scores Table */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Scoring Reference</span>
            <h2 className="font-serif text-4xl font-medium text-slate-900 mt-3 mb-6">API Scores for Academic Activities</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-600 max-w-2xl mx-auto">
              As per UGC Guidelines - Plan your publications strategically to maximize your score
            </p>
          </div>

          {/* Research Publications Table */}
          <div className="mb-12">
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <FileText className="w-6 h-6 text-[#C5A059]" />
              Research Publications
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-slate-200">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-4 py-3 text-left">Type of Publication</th>
                    <th className="px-4 py-3 text-center">API Score (Per Paper)</th>
                    <th className="px-4 py-3 text-center">Max Score (Per Period)</th>
                    <th className="px-4 py-3 text-left">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Research Paper in International Journal (Scopus/WoS)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-[#C5A059] text-slate-950 px-3 py-1 font-bold">15</span></td>
                    <td className="px-4 py-3 text-center">30</td>
                    <td className="px-4 py-3 text-sm text-slate-500">High impact factor journals carry higher weightage</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Research Paper in UGC CARE / International Journal</td>
                    <td className="px-4 py-3 text-center"><span className="bg-blue-500 text-white px-3 py-1 font-bold">10</span></td>
                    <td className="px-4 py-3 text-center">20</td>
                    <td className="px-4 py-3 text-sm text-slate-500">UGC CARE listed journals are considered</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Research Paper in National Journal (UGC CARE Listed)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-purple-500 text-white px-3 py-1 font-bold">5</span></td>
                    <td className="px-4 py-3 text-center">10</td>
                    <td className="px-4 py-3 text-sm text-slate-500">National level peer reviewed journals</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Conference Paper (International)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-emerald-500 text-white px-3 py-1 font-bold">5</span></td>
                    <td className="px-4 py-3 text-center">10</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Paper must be peer reviewed with ISBN</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Conference Paper (National)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-orange-500 text-white px-3 py-1 font-bold">3</span></td>
                    <td className="px-4 py-3 text-center">6</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Paper must be peer reviewed with ISBN</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Books & Chapters Table */}
          <div className="mb-12">
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-[#C5A059]" />
              Books & Chapters
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-slate-200">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-4 py-3 text-left">Type of Publication</th>
                    <th className="px-4 py-3 text-center">API Score</th>
                    <th className="px-4 py-3 text-center">Max Score (Per Period)</th>
                    <th className="px-4 py-3 text-left">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Book Authored (International Publisher)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-[#C5A059] text-slate-950 px-3 py-1 font-bold">20</span></td>
                    <td className="px-4 py-3 text-center">40</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Full length book with ISBN</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Book Authored (National Publisher)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-blue-500 text-white px-3 py-1 font-bold">10</span></td>
                    <td className="px-4 py-3 text-center">20</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Full length book with ISBN</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Chapter in Edited Book (International)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-purple-500 text-white px-3 py-1 font-bold">5</span></td>
                    <td className="px-4 py-3 text-center">10</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Edited book with ISBN</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Chapter in Edited Book (National)</td>
                    <td className="px-4 py-3 text-center"><span className="bg-emerald-500 text-white px-3 py-1 font-bold">2</span></td>
                    <td className="px-4 py-3 text-center">4</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Edited book with ISBN</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Patents & Other Table */}
          <div>
            <h3 className="font-serif text-2xl font-medium text-slate-900 mb-6 flex items-center gap-3">
              <Award className="w-6 h-6 text-[#C5A059]" />
              Patents & Other Activities
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full bg-white border border-slate-200">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-4 py-3 text-left">Type of Activity</th>
                    <th className="px-4 py-3 text-center">API Score</th>
                    <th className="px-4 py-3 text-center">Max Score (Per Period)</th>
                    <th className="px-4 py-3 text-left">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Patent Filed</td>
                    <td className="px-4 py-3 text-center"><span className="bg-blue-500 text-white px-3 py-1 font-bold">10</span></td>
                    <td className="px-4 py-3 text-center">20</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Published patent application</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Patent Granted</td>
                    <td className="px-4 py-3 text-center"><span className="bg-[#C5A059] text-slate-950 px-3 py-1 font-bold">20</span></td>
                    <td className="px-4 py-3 text-center">40</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Government / Recognized patent grant</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">Copyright Registered</td>
                    <td className="px-4 py-3 text-center"><span className="bg-purple-500 text-white px-3 py-1 font-bold">5</span></td>
                    <td className="px-4 py-3 text-center">10</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Registered copyrights only</td>
                  </tr>
                  <tr className="border-b border-slate-200 hover:bg-slate-50 bg-slate-50">
                    <td className="px-4 py-3 font-medium">Trademark Registered</td>
                    <td className="px-4 py-3 text-center"><span className="bg-emerald-500 text-white px-3 py-1 font-bold">5</span></td>
                    <td className="px-4 py-3 text-center">10</td>
                    <td className="px-4 py-3 text-sm text-slate-500">Registered trademarks only</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Infographic 2 - Every Publication Counts */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-medium text-slate-900 mb-4">Every Publication Counts</h2>
            <p className="text-slate-600">Detailed API scoring for various academic activities</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/praegleh_WhatsApp%20Image%202026-06-10%20at%2010.45.58%20PM%20%281%29.jpeg" 
              alt="API Scores for Various Academic Activities"
              className="max-w-4xl w-full shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Promotion Plans */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Strategic Planning</span>
            <h2 className="font-serif text-4xl font-medium text-white mt-3 mb-6">Plan Your Promotion Journey</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto mb-6" />
            <p className="text-slate-400 max-w-2xl mx-auto">
              Recommended publication strategy to achieve your API score targets
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Plan 1 */}
            <Card className="bg-slate-900 border border-slate-800 rounded-none overflow-hidden">
              <div className="bg-blue-600 p-4 text-center">
                <span className="text-white text-sm font-medium">PLAN 1</span>
                <h3 className="text-white font-serif text-xl mt-1">Assistant Professor</h3>
                <p className="text-blue-200 text-sm">Stage 1 → Stage 2</p>
              </div>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-400 text-sm">Minimum API Required</p>
                  <p className="text-4xl font-bold text-[#C5A059]">75</p>
                  <p className="text-slate-500 text-xs mt-1">4 Years Assessment</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Book (with ISBN)</span>
                    <span>2 × 20 = 40</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Edited Book</span>
                    <span>2 × 15 = 30</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Book Chapter</span>
                    <span>2 × 10 = 20</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Scopus Paper</span>
                    <span>1 × 15 = 15</span>
                  </div>
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between font-bold text-white">
                    <span>Total Max</span>
                    <span className="text-[#C5A059]">100+</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan 2 */}
            <Card className="bg-slate-900 border border-slate-800 rounded-none overflow-hidden">
              <div className="bg-purple-600 p-4 text-center">
                <span className="text-white text-sm font-medium">PLAN 2</span>
                <h3 className="text-white font-serif text-xl mt-1">Assistant Professor</h3>
                <p className="text-purple-200 text-sm">Stage 2 → Stage 3</p>
              </div>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-400 text-sm">Minimum API Required</p>
                  <p className="text-4xl font-bold text-[#C5A059]">150</p>
                  <p className="text-slate-500 text-xs mt-1">5 Years Assessment</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Book (with ISBN)</span>
                    <span>3 × 20 = 60</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Edited Book</span>
                    <span>2 × 15 = 30</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Book Chapter</span>
                    <span>4 × 10 = 40</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Scopus/WoS Paper</span>
                    <span>2 × 15 = 30</span>
                  </div>
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between font-bold text-white">
                    <span>Total Max</span>
                    <span className="text-[#C5A059]">200+</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan 3 */}
            <Card className="bg-slate-900 border border-slate-800 rounded-none overflow-hidden">
              <div className="bg-emerald-600 p-4 text-center">
                <span className="text-white text-sm font-medium">PLAN 3</span>
                <h3 className="text-white font-serif text-xl mt-1">Associate Professor</h3>
                <p className="text-emerald-200 text-sm">Stage 3 → Assoc. Prof</p>
              </div>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-400 text-sm">Minimum API Required</p>
                  <p className="text-4xl font-bold text-[#C5A059]">300</p>
                  <p className="text-slate-500 text-xs mt-1">3 Years Assessment</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Book (with ISBN)</span>
                    <span>4 × 20 = 80</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Edited Book</span>
                    <span>3 × 15 = 45</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Book Chapter</span>
                    <span>5 × 10 = 50</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Scopus/WoS Paper</span>
                    <span>4 × 15 = 60</span>
                  </div>
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between font-bold text-white">
                    <span>Total Max</span>
                    <span className="text-[#C5A059]">400+</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan 4 */}
            <Card className="bg-slate-900 border border-slate-800 rounded-none overflow-hidden">
              <div className="bg-[#C5A059] p-4 text-center">
                <span className="text-slate-950 text-sm font-medium">PLAN 4</span>
                <h3 className="text-slate-950 font-serif text-xl mt-1">Professor</h3>
                <p className="text-slate-800 text-sm">Assoc. Prof → Professor</p>
              </div>
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <p className="text-slate-400 text-sm">Minimum API Required</p>
                  <p className="text-4xl font-bold text-[#C5A059]">500</p>
                  <p className="text-slate-500 text-xs mt-1">3 Years Assessment</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-slate-300">
                    <span>Book (with ISBN)</span>
                    <span>5 × 20 = 100</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Edited Book</span>
                    <span>4 × 15 = 60</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Book Chapter</span>
                    <span>8 × 10 = 80</span>
                  </div>
                  <div className="flex justify-between text-slate-300">
                    <span>Scopus/WoS Paper</span>
                    <span>6 × 15 = 90</span>
                  </div>
                  <div className="border-t border-slate-700 pt-2 mt-2 flex justify-between font-bold text-white">
                    <span>Total Max</span>
                    <span className="text-[#C5A059]">700+</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Infographic 3 - Publication Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-medium text-slate-900 mb-4">Your Publications. Your Promotion. Your Success.</h2>
            <p className="text-slate-600">Detailed promotion plans with publication requirements</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/xf5a77ua_WhatsApp%20Image%202026-06-10%20at%2010.45.57%20PM%20%281%29.jpeg" 
              alt="API Scores for Publications - Promotion Plans"
              className="max-w-4xl w-full shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* Tips for Academicians */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C5A059] text-sm font-medium tracking-widest uppercase">Expert Advice</span>
            <h2 className="font-serif text-4xl font-medium text-slate-900 mt-3 mb-6">Research Strategy Tips</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Prioritize High-Impact Publications</h3>
              <p className="text-slate-600 text-sm">
                Focus on Scopus/Web of Science indexed journals for maximum API score. A single paper in a high-impact journal (15 points) equals three national-level papers (5 points each).
              </p>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <TrendingUp className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Balance Your Portfolio</h3>
              <p className="text-slate-600 text-sm">
                Maintain a mix of authored books, edited volumes, research papers, and book chapters. This diversification protects against rejection in any single category and maximizes overall score.
              </p>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Plan Ahead</h3>
              <p className="text-slate-600 text-sm">
                Start planning 1-2 years before your assessment period. Journal publications can take 6-12 months from submission to publication. Book authoring needs even more lead time.
              </p>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Consider Co-authorship Rules</h3>
              <p className="text-slate-600 text-sm">
                For joint publications, API scores are typically divided among authors. Single-author publications give full points. Plan your collaborations strategically.
              </p>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <Lightbulb className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Don't Ignore Patents & IPR</h3>
              <p className="text-slate-600 text-sm">
                Patents carry significant API scores (10-20 points). If your research has commercial applications, consider filing patents alongside publications.
              </p>
            </div>

            <div className="bg-white p-8 shadow-lg">
              <div className="w-14 h-14 bg-[#C5A059] flex items-center justify-center mb-6">
                <Calculator className="w-7 h-7 text-slate-950" />
              </div>
              <h3 className="font-serif text-xl font-medium text-slate-900 mb-4">Track Maximum Limits</h3>
              <p className="text-slate-600 text-sm">
                Each category has a maximum score cap per assessment period. Once you hit the cap, additional publications won't add points. Spread your work across categories.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Infographic 4 - Assessment Periods */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-medium text-slate-900 mb-4">Every Activity Counts</h2>
            <p className="text-slate-600">Understanding assessment periods and scoring guidelines</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_472d7425-aa73-46af-9a67-085a58da3658/artifacts/n86gqcnm_WhatsApp%20Image%202026-06-10%20at%2010.45.59%20PM.jpeg" 
              alt="API Scores for Various Activities - Assessment Guide"
              className="max-w-4xl w-full shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-medium text-slate-900 mb-6">How SPA Publication Group Helps</h2>
            <div className="w-16 h-[3px] bg-[#C5A059] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">Research Papers</h3>
              <p className="text-slate-500 text-sm">UGC CARE, Scopus, WoS indexed journal publications</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">Books & Chapters</h3>
              <p className="text-slate-500 text-sm">ISBN books, edited volumes, and book chapters</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">Conferences</h3>
              <p className="text-slate-500 text-sm">National & international conference proceedings</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-slate-950 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-[#C5A059]" />
              </div>
              <h3 className="font-serif text-lg font-medium text-slate-900 mb-2">Patents & IPR</h3>
              <p className="text-slate-500 text-sm">Patent filing, copyrights, and trademarks</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gradient-to-r from-[#C5A059] via-[#d4b06a] to-[#C5A059]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl font-medium text-slate-950 mb-6">
            Maximize Your API Score Today!
          </h2>
          <p className="text-slate-800 text-lg mb-10 max-w-2xl mx-auto">
            Let us help you plan your research strategy and achieve your career advancement goals. Contact us for expert guidance on publications.
          </p>
          
          <div className="flex flex-wrap justify-center gap-6 mb-10">
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-8 py-5 font-bold text-lg transition-colors shadow-xl"
              data-testid="api-contact-whatsapp"
            >
              <MessageCircle className="w-6 h-6" /> Contact on WhatsApp
            </a>
            <a 
              href="mailto:spapublication@gmail.com"
              className="flex items-center gap-3 bg-slate-950 hover:bg-slate-800 text-white px-8 py-5 font-bold text-lg transition-colors shadow-xl"
              data-testid="api-contact-email"
            >
              <Mail className="w-6 h-6" /> Email Us
            </a>
          </div>

          <div className="bg-white/20 backdrop-blur-sm p-6 inline-block">
            <div className="flex flex-wrap justify-center gap-8 text-slate-950">
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">+91 6284607500</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span className="font-medium">spapublication@gmail.com</span>
              </div>
            </div>
          </div>

          <p className="text-slate-800 text-sm mt-8">
            <strong>YOUR RESEARCH. OUR PUBLICATION. YOUR SUCCESS.</strong>
          </p>
        </div>
      </section>
    </div>
  );
}
